/**
 * auth/access — 역할 계층·메뉴 접근 규칙 단일 SoT (2026-07-24 PIN 대체).
 *
 * 4역할(계층):
 *   owner(사장)    : 전부(정산 포함 + 이견 SMBM)
 *   exec(진현)     : 관리자 권한 + SMBM 접근(별도 사업자=진현 데이터, 정산 제외)
 *                    — 실무상 이 등급 = 하도급사 진현. 라벨도 '진현'(사장 확정 2026-07-26)
 *   manager(관리자): 정산 제외 전부(설정·직원관리·계약·전자계약)
 *   staff(직원)    : 견적 + 잔금내역서(2026-07-26 개방)
 *
 * 백엔드 정본은 routers/staff._role_of. 여기선 메뉴 숨김·라우트 가드용 사본.
 * AUTH 미설정(공용 비번 모드)일 땐 role=owner 로 취급 → 전 메뉴 노출(기존 동작 보존).
 */
export const ROLE_RANK = { field: 1, staff: 2, manager: 3, exec: 4, owner: 5, admin: 5 };

/** role 이 min 이상인가. 미지 role 은 staff 로 간주(하위호환 — field 는 명시된 계정만). */
export function roleAtLeast(role, min) {
  return (ROLE_RANK[role] || 2) >= (ROLE_RANK[min] || 2);
}

/**
 * 단가 DB **편집** 최소 역할 — 열람(`recipedb` 기능 권한)과 별개다.
 *
 * 저장 엔드포인트 `PUT /api/recipe-db/update` 가 `_require_role(request, "admin")`
 * 이라 owner/admin 전용인데, FE 는 recipedb 를 manager 부터 열어 편집 UI 까지
 * 다 보여줬다 — 관리자·진현은 **저장 순간에만** 실패했다(감사 2026-07-29 #85).
 * 사장 결정 2026-07-31 "단가db는 전체 열람해도 된다" → 열람은 그대로 두고
 * 편집만 백엔드와 같은 문턱으로 좁힌다.
 */
export const RECIPE_DB_EDIT_MIN_ROLE = "owner";   // ROLE_RANK 5 = 백엔드 admin 과 동일

/** 이 역할이 단가를 고칠 수 있는가(열람 가능 여부와 무관). */
export function canEditRecipeDb(role) {
  return roleAtLeast(role, RECIPE_DB_EDIT_MIN_ROLE);
}

/** 역할 표시명 단일 SoT — StaffAdmin 의 표·선택지도 전부 여기서 파생(중복 금지). */
export const ROLE_LABEL = { owner: "대표", exec: "진현", manager: "관리자", staff: "직원", field: "현장팀" };
/** 낮은 → 높은 순. 표 컬럼·선택지 순서의 정본. */
export const ROLE_ORDER = ["field", "staff", "manager", "exec", "owner"];

/**
 * 접근 권한 config — 기능별 '최소 역할'. 사장이 직원 관리에서 조정(app_config/access).
 * def = 현재 하드코딩 기본값(무변경 보장). 서버 override 가 있으면 그걸 우선.
 * 게이트 대상만 정의 — 그 외(설정›테마 등)는 전원(config 없음).
 */
export const ACCESS_FEATURES = [
  // 현장팀(field) 권한 최소화(사장 확정 2026-07-27) — 견적 계열은 직원(staff)부터.
  { key: "estimate",   label: "견적 만들기·목록·프랜차이즈·클립보드·휴지통", def: "staff" },
  // QCC·기타문서는 현장에서도 쓰는 도구 — 견적 계열에서 분리해 현장팀 개방(사장 지시 2026-07-28).
  { key: "qcc",        label: "QCC",            def: "field" },
  { key: "docs",       label: "기타문서",         def: "field" },
  // ⚠진현(exec)은 **외부 하도급사**다 — 등급은 관리자보다 위지만 계약서·전자계약은
  //   보지 않는다(사장 결정 2026-07-31). 등급 임계만으로는 표현할 수 없어
  //   기능별 "역할 제외"를 둔다(deny).
  { key: "contract",   label: "계약서",          def: "manager", deny: ["exec"] },
  { key: "esign",      label: "전자계약",         def: "manager", deny: ["exec"] },
  // 잔금내역서 = 현장 직원도 봐야 하는 문서(사장 확정 2026-07-26) — 계약서/전자계약과 분리.
  { key: "balance",    label: "잔금내역서",       def: "staff" },
  { key: "settlement", label: "정산",            def: "owner" },
  { key: "smbm",       label: "SMBM(머니앱)",    def: "exec" },
  { key: "smbm_lite",  label: "SMBM lite(보조 사업자)", def: "owner" },
  // 현장 직원이 as.igggstudio.com 에서 바로 쓰는 화면 — staff 개방(사장 2026-07-27).
  // 삭제만 서버에서 manager 이상으로 막는다.
  { key: "as_ops",     label: "AS유지보수 관리",  def: "field" },
  { key: "pipeline",   label: "영업 파이프라인",   def: "owner" },
  { key: "recipedb",   label: "단가 DB",         def: "manager" },
  { key: "brands",     label: "브랜드",          def: "manager" },
  { key: "unmatched",  label: "미등록 단가 요청",  def: "manager" },
  { key: "bizinfo",    label: "사업자 정보",       def: "owner" },
  { key: "esignseals", label: "전자계약 서명",     def: "owner" },
  { key: "staffadmin", label: "직원 관리",        def: "owner" },
];
export const DEFAULT_ACCESS = Object.fromEntries(ACCESS_FEATURES.map((f) => [f.key, f.def]));

// 서버가 이 역할보다 낮은 계정을 허용하지 않는 기능. 설정 화면에서 최소 역할을 더
// 낮춰도 UI만 열리고 API가 403이 되는 거짓 권한을 만들지 않는다.
export const SERVER_ROLE_FLOORS = { esign: "manager" };
export const SERVER_ROLE_DENY = { contract: ["exec"], esign: ["exec"] };

/** 기능별 **제외 역할** — 등급 임계와 별개 축(사장 결정 2026-07-31).
 *  진현(exec)은 등급상 관리자 위지만 외부 하도급사라 계약 계열을 보지 않는다. */
export const DEFAULT_DENY = Object.fromEntries(
  ACCESS_FEATURES.filter((f) => f.deny?.length).map((f) => [f.key, f.deny]),
);

/** 서버 config(_deny)를 기본 제외 위에 병합. 유효 역할만 인정. */
export function mergeDeny(cfg) {
  const out = { ...DEFAULT_DENY };
  const src = cfg && typeof cfg === "object" ? cfg._deny : null;
  if (src && typeof src === "object") {
    for (const [k, v] of Object.entries(src)) {
      if (!(k in DEFAULT_ACCESS)) continue;
      if (Array.isArray(v)) out[k] = v.filter((r) => r in ROLE_RANK);
    }
  }
  for (const [key, roles] of Object.entries(SERVER_ROLE_DENY)) {
    out[key] = [...new Set([...(out[key] || []), ...roles])];
  }
  return out;
}

export function deniedFor(deny, key) {
  return [...new Set([
    ...((deny && deny[key]) || DEFAULT_DENY[key] || []),
    ...(SERVER_ROLE_DENY[key] || []),
  ])];
}

/** 최종 접근 판정 — **등급 임계 통과 + 제외 아님**. 게이트는 전부 이걸 쓴다. */
export function canAccess(role, key, access, deny) {
  if (deniedFor(deny, key).includes(role)) return false;
  return roleAtLeast(role, minRoleFor(access, key));
}

/** 서버 config 를 기본값 위에 병합(유효 role 만). */
export function mergeAccess(cfg) {
  const out = { ...DEFAULT_ACCESS };
  if (cfg && typeof cfg === "object") {
    for (const k of Object.keys(DEFAULT_ACCESS)) {
      if (ROLE_ORDER.includes(cfg[k])) out[k] = cfg[k];
    }
  }
  return out;
}

/** access map + 기능키 → 최소 역할(없으면 기본값). */
export function minRoleFor(access, key) {
  const configured = (access && access[key]) || DEFAULT_ACCESS[key] || "staff";
  const floor = SERVER_ROLE_FLOORS[key];
  if (floor && (ROLE_RANK[configured] || 0) < (ROLE_RANK[floor] || 0)) return floor;
  return configured;
}
